import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {SlotFormula} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIcons, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {
    FunctionsMenuItems,
    FunctionsMenuItemType,
    IFunctionsMenuItem
} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/header/topFormulaEditor/FunctionsMenuItems';
import {FunctionSpec} from '@mintdata/mint-kit/api/decorators/Function';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {DataSource, IListItemState} from '@mintdata/mint-kit/api/models/List';
import {PropertyUtils} from '@mintdata/mint-kit/api/properties/PropertyUtils';
import {ContextMenu, ToggleOn} from '@mintdata/mint-kit/impl/components/contextMenu/ContextMenu';
import {PopupAnchor, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {IStyles} from '@mintdata/mint-kit/impl/util/Styles';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {computed} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./FunctionsMenu.less');
const cx = classnames.bind(styles);

export interface IFunctionsMenuProps
{

}

export interface IFunctionsMenuState
{
    isOpened:boolean;
    openedSubMenuId:string;
}

@observer
export class FunctionsMenu extends React.Component<IFunctionsMenuProps, IFunctionsMenuState>
{
    public constructor(props:any)
    {
        super(props);

        this.state = {
            isOpened: false,
            openedSubMenuId: ''
        };
    }

    private _onShow = () => this.setState({isOpened: true});
    private _onHide = () =>
    {
        this.setState({isOpened: false});

        const {mesh} = controllers.ui.spreadsheets;
        mesh.focusMeshOrControl(this._focusedMeshCtx);
    };

    private _setOpenedSubMenuId = (id:string) =>
    {
        this.setState({openedSubMenuId: id});
    };

    public render():JSX.Element
    {
        const togglerId = 'functionsSelector';
        const {isOpened} = this.state;

        return (
            <div className={cx('base')} id={togglerId}>
                <ContextMenu
                    toggleOn={[ToggleOn.CLICK]}
                    togglersIds={[togglerId]}
                    popupAnchor={PopupAnchor.LeftTop}
                    sourceAnchor={SourceAnchor.LeftBottom}
                    popupClassName={cx('menuPopup')}
                    dataSource={this._menuProvider}
                    itemRenderer={this._renderMenuItem}
                    onShow={this._onShow}
                    onHide={this._onHide}
                >
                    <div className={cx('handler', {isOpened})}>
                        <WSPIcon
                            size={WSPIconSize.medium20}
                            className={cx('sumIcon')}
                            name={WSPIcons.visualDesigner.spreadsheet.Sum}
                        />
                        <MDIcon
                            className={cx('menuIcon', {isOpened})}
                            name={MDIcons.common.ArrowDropdown}
                            size={MDIconSize.medium20}
                        />
                    </div>
                </ContextMenu>
            </div>
        );
    }

    @bind
    private _menuProvider():DataSource<IFunctionsMenuItem>
    {
        const menuItems = FunctionsMenuItems.map((item:IFunctionsMenuItem) =>
        {
            if( !item.type ) return null;

            let {id, isSeparator, label, handler, functions, funcName, items} = item;

            if( !id ) id = IdUtil.newGUID();

            switch( item.type )
            {
                case FunctionsMenuItemType.Separator:

                    isSeparator = true;
                    break;

                case FunctionsMenuItemType.Function:

                    handler = () => this._selectFunction(funcName);
                    break;

                case FunctionsMenuItemType.SingleItem:

                    // TODO: ?
                    break;

                case FunctionsMenuItemType.Group:

                    if( !functions ) break;

                    const groupFuncNames:FunctionSpec[] = [];

                    functions.map(containerCtor =>
                    {
                        const containerProto = containerCtor.prototype;
                        const container = new containerCtor();
                        const namespace = container.namespace;
                        const functionMap = PropertyUtils.getFunctionMap(container);

                        const funcNames = Object.keys(containerProto);

                        // get name from FunctionContainer namespace
                        if( functions.length === 1 && !label )
                            label = namespace.toLowerCase().charAt(0).toUpperCase() + namespace.substring(1);

                        funcNames.forEach(funcName =>
                        {
                            if( funcName.charAt(0) !== '_' && funcName.toLowerCase() !== 'namespace' )
                            {
                                const funcSpec:FunctionSpec = functionMap.findFunctionSpec(funcName);

                                groupFuncNames.push(funcSpec);
                            }
                        });
                    });

                    items = [];

                    groupFuncNames
                        .sort((a, b) => a.alias > b.alias ? 1 : -1)
                        .forEach(funcSpec =>
                        {
                            items.push({
                                label: funcSpec.alias.toUpperCase(),
                                funcName: funcSpec.alias,
                                tip: funcSpec.tip,
                                type: FunctionsMenuItemType.Function,
                                handler: () => this._selectFunction(funcSpec.alias)
                            });
                        });
                    break;
            }

            return {
                ...item,
                id,
                label,
                isSeparator,
                items,
                handler
            } as IFunctionsMenuItem;
        });

        menuItems.push(this._learnMoreMenuItem());

        return menuItems;
    }

    @bind
    private _learnMoreMenuItem():IFunctionsMenuItem
    {
        return {
            label: 'Learn more',
            type: FunctionsMenuItemType.SingleItem,
            handler: () =>
            {
                // TODO: learn more ?
            }
        };
    }

    @bind
    private _selectFunction(funcName:string)
    {
        if( !this._focusedMeshState ) return;

        const {mesh} = controllers.ui.spreadsheets;

        const formula:SlotFormula = `=${funcName.toUpperCase()}()`;

        mesh.startEditingCell(this._focusedMeshCtx, formula, formula.length - 1, true);
    }

    @bind
    private _renderMenuItem(item:IFunctionsMenuItem,
                            {isSelected}:IListItemState,
                            {menuItem, separator, menuItemHover}:IStyles):JSX.Element
    {
        const {id, isSeparator} = item;

        if( isSeparator ) return (
            <div className={cx(separator)} />
        );

        const {openedSubMenuId} = this.state;

        const openedStyle = id === openedSubMenuId ? menuItemHover : '';

        return (
            <div className={cx('menuItem', menuItem, openedStyle)} id={id}>

                {this._renderSimpleMenuItem(item)}
                {this._renderGroupMenuItem(item, id)}

            </div>
        );
    }

    @bind
    private _renderSimpleMenuItem(item:IFunctionsMenuItem):JSX.Element
    {
        const {type, label, tip} = item;

        if( type && type === FunctionsMenuItemType.Group ) return null;

        return (
            <div className={cx('menuItemLabel')}>
                {label}
                {
                    tip &&
                    <div className={cx('menuItemTip')}>
                        {tip}
                    </div>
                }
            </div>
        );
    }

    @bind
    private _renderGroupMenuItem(item:IFunctionsMenuItem, togglerId:string):JSX.Element
    {
        const {type, label, items} = item;

        if( !(type && type === FunctionsMenuItemType.Group) ) return null;

        return (
            <ContextMenu
                className={cx('groupMenuItem')}
                toggleOn={[ToggleOn.HOVER, ToggleOn.CLICK]}
                togglersIds={[togglerId]}
                popupAnchor={PopupAnchor.LeftTop}
                sourceAnchor={SourceAnchor.RightTop}
                popupClassName={cx('menuPopupGroup')}
                dataSource={items}
                itemRenderer={this._renderMenuItem}
                onShow={() => this._setOpenedSubMenuId(togglerId)}
                onHide={() => this._setOpenedSubMenuId('')}
            >
                <div className={cx('menuItemLabelWrapper')}>
                    <div className={cx('menuItemLabel')}>
                        {label}
                    </div>
                    <MDIcon
                        className={cx('iconRight')}
                        name={MDIcons.common.ArrowDropright}
                        size={MDIconSize.medium20}
                    />
                </div>
            </ContextMenu>
        );
    }

    @computed
    private get _focusedSheetId():SheetId
    {
        const {main} = controllers.ui.spreadsheets;

        const workbook = main.getRootWorkbook();
        if( !workbook ) return;

        return workbook.view.focusedSheetId;
    }

    @computed
    private get _activeMeshState():MeshState
    {
        const focusedSheetId:SheetId = this._focusedSheetId;
        if( !focusedSheetId ) return;

        const {mesh} = controllers.ui.spreadsheets;
        return mesh.getState(this._focusedMeshCtx);
    }

    @computed
    private get _focusedMeshCtx():IWSPMeshContext
    {
        return controllers.ui.spreadsheets.workbook.getMeshContextBySheetId(this._focusedSheetId);
    }

    @computed
    private get _focusedMeshState():MeshState
    {
        return this._focusedMeshCtx && controllers.ui.spreadsheets.mesh.getState(this._focusedMeshCtx);
    }
}
